import { useInfiniteQuery } from '@tanstack/react-query';
import { apiClient } from 'lib/api';
import { isAxiosError } from 'axios';
import queryKeys from './queryKeys';
import queryString from 'query-string';

export interface GetCampaignsParams {
  facebookAccountId: string[];
  campaignNameFilter?: string;
  campaign_ids?: string[];
  status?: RowStatus;
  /** Name terms typed by the user; the backend ANDs them as name-contains filters. */
  query?: string[];
}

export type RowStatus = 'ACTIVE' | 'PAUSED';

export interface CampaignEntry {
  id: string;
  name: string;
  status: RowStatus;
  budget: number;
  is_cbo: boolean;
}

interface ErrorResponse {
  message: string;
}

export const getCampaigns = async (
  {
    params,
  }: {
    params: GetCampaignsParams;
  },
  signal: AbortSignal,
): Promise<CampaignEntry[]> => {
  const { facebookAccountId, campaignNameFilter, status, campaign_ids, query } = params;

  const promises = facebookAccountId.map((id) =>
    apiClient.get<CampaignEntry[]>(
      `/api/v1/facebook_manager/get_campaigns/${id}/`,
      {
        params: {
          campaign_name_filter: campaignNameFilter,
          ...(campaign_ids?.length ? { campaign_ids } : {}),
          ...(status ? { status } : {}),
          ...(query?.length ? { query } : {}),
        },
        signal,
        paramsSerializer: (prms) =>
          queryString.stringify(prms, { arrayFormat: 'comma' }),
      },
    ),
  );

  try {
    const responses = await Promise.all(promises);

    const isError = responses.some((r) => isAxiosError(r));
    if (isError) {
      const errorResponses: ErrorResponse[] = responses.filter(
        (response, index) => {
          const error = isAxiosError(response);
          if (error) {
            response.message = `An error occurred while fetching campaigns for account ${facebookAccountId[index]}`;
          }
          return error;
        },
      ) as unknown as ErrorResponse[];

      // eslint-disable-next-line @typescript-eslint/prefer-promise-reject-errors
      return Promise.reject(errorResponses);
    }

    // Filter out successful responses and handle errors
    const successfulResponses = responses.filter(
      (response) => response?.status >= 200 && response?.status <= 400,
    );
    // Return the combined data from successful responses
    return successfulResponses.flatMap((response) => response.data);
  } catch (error) {
    // Handle any other unexpected errors
    console.error(error);
    throw error;
  }
};

export const dedupeCampaignsById = (
  items: readonly CampaignEntry[],
): CampaignEntry[] => {
  const byId = new Map<string, CampaignEntry>();
  for (const item of items) {
    if (item?.id) {
      byId.set(item.id, item);
    }
  }
  return [...byId.values()];
};

export const CAMPAIGNS_PAGE_SIZE = 500;

/** Next-page cursor (`X-Next-Cursor`) per ad account; only accounts that still have more pages. */
export type CampaignCursors = Record<string, string>;

export interface CampaignsPage {
  items: CampaignEntry[];
  cursors: CampaignCursors;
}

export const getCampaignsPage = async (
  {
    params,
    cursors,
  }: {
    params: Omit<GetCampaignsParams, 'campaign_ids'>;
    cursors?: CampaignCursors;
  },
  signal: AbortSignal,
): Promise<CampaignsPage> => {
  const { facebookAccountId, campaignNameFilter, status, query } = params;
  const accountIds = cursors ? Object.keys(cursors) : facebookAccountId;

  const responses = await Promise.all(
    accountIds.map((id) =>
      apiClient.get<CampaignEntry[]>(
        `/api/v1/facebook_manager/get_campaigns/${id}/`,
        {
          params: {
            campaign_name_filter: campaignNameFilter,
            ...(status ? { status } : {}),
            ...(query?.length ? { query } : {}),
            limit: CAMPAIGNS_PAGE_SIZE,
            ...(cursors ? { after: cursors[id] } : {}),
          },
          signal,
          paramsSerializer: (prms) =>
            queryString.stringify(prms, { arrayFormat: 'comma' }),
        },
      ),
    ),
  );

  const nextCursors: CampaignCursors = {};
  responses.forEach((response, index) => {
    const next: unknown = response.headers?.['x-next-cursor'];
    if (typeof next === 'string' && next) {
      nextCursors[accountIds[index]] = next;
    }
  });

  return {
    items: responses.flatMap((response) => response.data),
    cursors: nextCursors,
  };
};

export const fetchFirstCampaignsPage = async (
  {
    params,
  }: { params: GetCampaignsParams },
  signal: AbortSignal,
): Promise<CampaignsPage> => {
  const { campaign_ids, query, ...baseParams } = params;
  const uniqueUrlCampaignIds = [...new Set(campaign_ids ?? [])].filter(Boolean);

  if (!uniqueUrlCampaignIds.length) {
    return getCampaignsPage({ params: { ...baseParams, query } }, signal);
  }

  // URL campaigns are pinned by id, so the name query must not narrow them.
  const [matchedByIds, basePage] = await Promise.all([
    getCampaigns({ params: { ...baseParams, campaign_ids: uniqueUrlCampaignIds } }, signal),
    getCampaignsPage({ params: { ...baseParams, query } }, signal),
  ]);

  return {
    items: dedupeCampaignsById([...basePage.items, ...matchedByIds]),
    cursors: basePage.cursors,
  };
};

interface UseCampaignsOptions {
  params: GetCampaignsParams;
  config?: { enabled?: boolean };
}

// `campaign_ids` is deliberately left out of the key: the browse list is the
// identity of this query, and the ids only add campaigns the filters can't
// reach. Keying on them would refetch the whole list every time the Campaigns
// step mirrors a selection change back to the URL. They are only sent with
// the first page.
export const useCampaigns = ({ params, config }: UseCampaignsOptions) => {
  return useInfiniteQuery({
    staleTime: 30_000,
    ...config,
    queryFn: ({ pageParam, signal }) =>
      pageParam
        ? getCampaignsPage({ params, cursors: pageParam }, signal)
        : fetchFirstCampaignsPage({ params }, signal),
    queryKey: [
      ...queryKeys.campaigns,
      'infinite',
      params.facebookAccountId,
      params.campaignNameFilter,
      params.status,
      params.query ?? [],
    ],
    initialPageParam: undefined as CampaignCursors | undefined,
    getNextPageParam: (lastPage) =>
      Object.keys(lastPage.cursors).length ? lastPage.cursors : undefined,
    select: (data) =>
      dedupeCampaignsById(data.pages.flatMap((page) => page.items)),
  });
};
